/** @format */

import React, { useCallback, useRef } from 'react'
import { Controlled as CodeMirror } from 'react-codemirror2'
import styled from 'styled-components'
import { Card, Copy, Message } from '@king-design/react'

// 导入 CodeMirror 样式和模式
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material.css'
import 'codemirror/theme/monokai.css'
import 'codemirror/theme/solarized.css'
import 'codemirror/mode/javascript/javascript'

interface JsonCodemirrorProps {
  value?: string
  onChange?: (value: string) => void
  height?: string | number
  theme?: 'default' | 'material' | 'monokai' | 'solarized'
  readOnly?: boolean
  placeholder?: string
  className?: string
}
/**
 * 检查字符串是否可以完整转成JSON
 * @param str 待检查的字符串
 * @returns { isValid: boolean, error?: string, parsed } 返回验证结果
 */
export const isValidJson = (str: string): { isValid: boolean; error?: string; parsed? } => {
  if (typeof str !== 'string') {
    return {
      isValid: false,
      error: '输入必须是字符串类型'
    }
  }
  if (!str.trim()) {
    return {
      isValid: true,
      parsed: {}
    }
  }

  try {
    const parsed = JSON.parse(str)
    return {
      isValid: true,
      parsed
    }
  } catch (error) {
    return {
      isValid: false,
      error: error instanceof Error ? error.message : 'JSON解析失败'
    }
  }
}

/**
 * 检查字符串是否是有效的JSON对象或数组（不包括原始值）
 * @param str 待检查的字符串
 * @returns { isValid: boolean, error?: string, parsed?: any } 返回验证结果
 */
export const isValidJsonObject = (str: string): { isValid: boolean; error?: string; parsed? } => {
  const result = isValidJson(str)
  if (!result.isValid) {
    return result
  }

  if (result.parsed !== null && typeof result.parsed === 'object') {
    return result
  }

  return {
    isValid: false,
    error: 'JSON必须是对象或数组，不能是原始值（数字、字符串、布尔值等）'
  }
}
const JsonCodemirror: React.FC<JsonCodemirrorProps> = ({
  value = '',
  onChange,
  height = '400px',
  theme = 'material',
  readOnly = false,
  placeholder = '请输入 JSON 数据...',
  className
}) => {
  const editorRef = useRef(null)

  // 格式化 JSON
  const formatJson = useCallback(() => {
    const { isValid, parsed, error } = isValidJsonObject(value)
    if (isValid) {
      const formatted = JSON.stringify(parsed, null, 2)
      onChange?.(formatted)
      Message.success('JSON 格式化成功')
    } else {
      error && Message.error(error || 'JSON 格式化失败')
    }
  }, [value, onChange])

  return (
    <div className={className}>
      <Card
        type="border"
        slotHeader={
          <div className="ksc-space">
            <Copy text={value}>
              <a>复制</a>
            </Copy>
            <a onClick={formatJson}>格式化</a>
          </div>
        }
      >
        <div className="json-editor-container">
          <CodeMirror
            value={value}
            onBeforeChange={(_editor, _data, value) => {
              onChange?.(value)
            }}
            options={{
              mode: 'application/json',
              theme,
              lineNumbers: true,
              lineWrapping: true,
              foldGutter: true,
              gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
              readOnly,
              placeholder,
              autoCloseBrackets: true,
              matchBrackets: true,
              indentUnit: 2,
              tabSize: 2,
              indentWithTabs: false,
              styleActiveLine: true,
              styleSelectedText: true
            }}
            editorDidMount={editor => {
              editorRef.current = editor
              // 设置编辑器高度
              if (typeof height === 'number') {
                editor.setSize('100%', height)
              } else {
                editor.setSize('100%', height)
              }
            }}
          />
        </div>
      </Card>
    </div>
  )
}

export default styled(JsonCodemirror)`
  .k-card-header {
    justify-content: flex-end;
  }
  .json-editor-container {
    .CodeMirror {
      height: ${props =>
        typeof props.height === 'number' ? `${props.height}px` : props.height || '400px'};
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 14px;
      line-height: 1.5;
    }

    .CodeMirror-linenumber {
      color: #999;
    }

    .CodeMirror-foldgutter {
      width: 12px;
    }

    .CodeMirror-foldgutter-open,
    .CodeMirror-foldgutter-folded {
      cursor: pointer;
    }

    .CodeMirror-foldgutter-open:after {
      content: '▼';
    }

    .CodeMirror-foldgutter-folded:after {
      content: '▶';
    }
  }
`
